import { isDevelopment } from '@/modules/env/isDevelopment';
import { isServerCheck } from '@/modules/env/isServerCheck';
import { isStaging } from '@/modules/env/isStaging';
import { resolveApiHost } from '@/modules/http/resolveApiHost';
import {
  type BuiltEndpoint,
  type PresentationApiEndpoints,
} from '@/modules/http/type-helpers';

export const PROD_HOST: string = 'https://www.depop.com';
export const STAGING_HOST: string = 'https://www-staging.depop.com';

export function buildEndpoint<T extends string = PresentationApiEndpoints>(
  path: T,
  config: { isRoutingProxy?: boolean } = {}
): BuiltEndpoint<T> {
  let apiHost = process.env.NEXT_PUBLIC_API_HOST;

  if (!config.isRoutingProxy) {
    apiHost = resolveApiHost(path);
  }

  return `${apiHost}${path}`;
}

// The Turnstile implementation requires proxy requests through the current domain,
// so this function was created to explicitly indicate that.
// see more details here: https://depopmarket.atlassian.net/wiki/spaces/Trust/pages/5225676905/Protecting+API+routes+with+WAF+and+Turnstile+architecture+pitfalls+and+the+working+approach
export function buildTurnstileEnabledEndpoint<
  T extends string = PresentationApiEndpoints,
>(path: T): BuiltEndpoint<T> {
  if (isStaging() || isDevelopment()) {
    return `${STAGING_HOST}${path}`;
  }

  return `${PROD_HOST}${path}`;
}

// Product search is split by runtime: browser requests use the public www
// origin so we have access to Cloudflare clearance cookies,
// while SSR/server requests continue using the existing internal search service route.
export function buildProductSearchEndpoint<
  T extends string = PresentationApiEndpoints,
>(path: T): BuiltEndpoint<T> {
  if (isServerCheck()) {
    return buildEndpoint(path);
  }

  return buildTurnstileEnabledEndpoint(path);
}

export function buildStaticUrl(path: string) {
  return process.env.NEXT_PUBLIC_DEPOP_CLOUDFRONT_HOST + path;
}

export function removeHostFromEndpoint(path: string) {
  return new URL(path).pathname;
}

export const AUTH_IDENTIFY_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/users/me/'
);
export const CHECKIN_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/users/me/checkin/'
);
export const LISTING_DETAILS_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/users/me/listing-details/'
);

export const REVOKE_TOKEN_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/oauth/token/revoke/'
);

export const REFERRAL_EVENT_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/events/referral-event/'
);
export const ACTIVITY_TRACKER_EVENT_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/events/'
);
export const ACTIVITY_TRACKER_SESSION_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/events/session/'
);
export const APPLE_LOGIN_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/login/apple/'
);
export const APPLE_CREDENTIALS_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/auth/apple/user-credentials/'
);
export const EBAY_AUTHORIZE_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/auth/ebay/authorize/'
);
export const EBAY_USER_CREDENTIALS_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/auth/ebay/user-credentials/'
);
export const EBAY_LOGIN_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/login/ebay/'
);
export const BAG_BASIC_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/bags/me/basic/'
);
export const BAG_DETAILED_ENDPOINT = buildEndpoint(
  '/presentation/api/v2/bags/me/detailed/'
);
export const BAG_GUEST_DETAILED_ENDPOINT = buildEndpoint(
  '/presentation/api/v2/bags/guest/'
);
export const UPDATE_BAG_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/bags/me/'
);
export const BUCKET_EXPERIMENTS_ENDPOINT = buildEndpoint(
  `/presentation/api/v1/features/experiments/`
);
export const PREVIEW_EXPERIMENTS_ENDPOINT = buildEndpoint(
  `/presentation/api/v1/features/experiments/preview/`
);
export const WEB_SEGMENT_CONSENT_ENDPOINT = buildEndpoint(
  `/internal/v1/consent/web/:uuid/`
);
export const USER_COUNTERS_ENDPOINT = buildEndpoint(
  `/presentation/api/v1/users/:userId/counters/`
);

export const GROUPS_BY_ID_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/attributes/groups/'
);
export const CONTENTFUL_ENDPOINT = buildEndpoint('/presentation/api/v1/cms/');

export const DISCOUNT_ENDPOINT = buildEndpoint('/api/v2/discounts/');

export const DISCOUNT_SHOP_WIDE_ENDPOINT = buildEndpoint(
  '/api/v2/discounts/shop/'
);

export const DISCOUNT_JOB_STATUS_ENDPOINT = buildEndpoint(
  '/api/v2/discounts/shop/:jobId/'
);

export const NUDGES_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/products/batch/:ids/nudges/'
);

export const PRODUCT_SEARCH_ENDPOINT = buildProductSearchEndpoint(
  '/presentation/api/v1/search/products/'
);

export const EXPLORE_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/search/explore/'
);

export const USER_RECOMMENDATIONS_FEED_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/users/me/recommendations/products/feed/'
);

export const FILTER_AGGREGATES = buildEndpoint(
  '/presentation/api/v2/products/aggregates/'
);

export const SIZE_FILTER_TREE = buildEndpoint(
  '/presentation/api/v1/search/filters/size/'
);

export const SHOP_FILTER_AGGREGATES = buildEndpoint(
  '/presentation/api/v1/shops/:sellerId/aggregates/'
);

export const CATEGORY_FILTERS = buildEndpoint(
  '/presentation/api/v2/search/filters/category/'
);

export const BILLING_AGREEMENT_TOKEN_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/users/me/onboarding/paypal-agreement/'
);

export const BILLING_AGREEMENT_SUBMISSION_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/users/me/onboarding/paypal-agreement/'
);

/**
 * Create custom Stripe user (seller onboarding), migrated to presentation-api (WEB-8805).
 * Snake_case request/response, 201 on success. See presentation-api#1001.
 */
export const CREATE_STRIPE_USER_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/users/me/settings/stripe/'
);

export const SELLER_STATUS_ENDPOINT = buildEndpoint(
  '/api/v1/sellerOnboarding/sellerStatus/'
);

/**
 * Depop-payments (Stripe) seller eligibility, migrated to presentation-api (WEB-8806).
 * Snake_case request/response. See presentation-api#989.
 */
export const STRIPE_ELIGIBILITY_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/users/me/settings/stripe-eligibility/'
);

export const SELLER_VACATION_ENDPOINT = buildEndpoint('/api/v1/vacation/me/');

export const RECEIPTS_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/receipts/'
);

export const RECEIPT_BY_ID_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/receipts/:receiptId/'
);

export const SALES_REPORT_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/users/me/sales-report/'
);

export const SELLER_HISTORICAL_DATA_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/analytics/sellers/historical/:metric/'
);

export const POTENTIAL_REVENUE_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/users/me/stats/potential-revenue/'
);

export const REVENUE_BREAKDOWN_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/analytics/sellers/revenue-breakdown/'
);

export const MFA_CREATE_CHALLENGE_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/login/mfa/devices/:deviceId/challenge/'
);

export const MFA_DEVICES_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/login/mfa/devices/'
);

export const PRE_MFA_TOKEN_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/login/mfa/pre-mfa-token/'
);

export const ATTRIBUTES_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/attributes/'
);

export const AUTHENTICITY_GUIDANCE_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/attributes/authenticity/guidance/'
);

export const BULK_LISTING_ENABLED_ENDPOINT = buildEndpoint(
  '/api/v1/bulkactions/listing/enabled/'
);

export const BULK_LISTING_ENDPOINT = buildEndpoint(
  '/api/v1/bulkactions/listing/'
);

export const SHOP_BY_USERNAME_ENDPOINT = buildEndpoint(
  '/api/v1/shop/:username/'
);

export const BATCH_SHOP_BY_USERNAME_ENDPOINT = buildEndpoint(
  '/api/v1/shop/batch/:usernames/'
);

export const SHOP_BY_ID_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/shops/:sellerId/'
);

export const SHOP_PRODUCTS_BY_USER_ID_ENDPOINT = buildEndpoint(
  '/api/v1/shop/:userId/products/'
);

export const SHOP_PRODUCTS_BY_SELLER_ID_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/shops/:sellerId/products/'
);

export const SEARCH_SHOP_PRODUCTS_BY_USER_ID_ENDPOINT = buildEndpoint(
  '/api/v1/shop/:userId/products/search/'
);

export const SHOP_REARRANGE_BY_ID = buildEndpoint(
  '/presentation/api/v1/shops/me/ordering/'
);

export const SHOP_BULK_SOLD_PRODUCTS_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/products/batch/:productIds/mark-sold/'
);

export const FOLLOWING_RELATIONSHIP_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/users/:userId/viewer-meta/'
);

export const BOOSTED_LISTINGS_BOOSTED_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/shops/me/boosted-products/'
);

export const DELETE_FOLLOWING_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/users/me/following/:userIdToUnfollow/'
);

export const FOLLOWERS_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/users/:userId/followers/'
);

export const UPDATE_FOLLOWING_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/users/me/following/'
);

export const FOLLOWING_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/users/:userId/following/'
);

export const USER_EDUCATION_VIEW_COUNTS_ENDPOINT = buildEndpoint(
  `/presentation/api/v1/users/me/education/view-counts/`
);

export const SIGNUP_TOKEN_VALIDATION_ENDPOINT = buildEndpoint(
  '/api/v1/users/verification/email/'
);

export const BOOSTED_IMPRESSIONS_ATTRIBUTION_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/products/batch/attributions/'
);

export const SHIPPING_PREFERENCES_ENDPOINT = buildEndpoint(
  `/api/v1/shipping-preferences/`
);

export const SHIPPING_PROVIDERS_ENDPOINT = buildEndpoint(
  `/api/v1/shipping-providers/`
);

export const ESTIMATED_SHIPPING_PROVIDERS_ENDPOINT = buildEndpoint(
  `/presentation/api/v1/shipping/providers/depop/`
);

export const MARK_AS_SHIPPED_ENDPOINT = buildEndpoint(
  `/presentation/api/v1/shipping/me/parcels/:parcelId/mark-shipped/`
);

export const MANUAL_SHIPPING_PROVIDERS_ENDPOINT = buildEndpoint(
  `/presentation/api/v1/shipping/providers/manual/`
);

export const SHIPPING_LABEL_DOWNLOAD_ENDPOINT = buildEndpoint(
  `/presentation/api/v1/shipping/me/parcels/:parcelId/label/`
);

export const BULK_SHIPPING_LABELS_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/shipping/labels/batch/'
);

export const ADD_USER_TO_CAMPAIGN_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/shipping/campaign/users/'
);

export const PUDO_SHIPPING_LOCATIONS_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/shipping/pudo/locations/'
);

export const PRESENTATION_USER_SETTINGS = buildEndpoint(
  '/presentation/api/v1/users/me/settings/'
);

export const DAC7_COMPLIANCE_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/tax/dac7/compliance/'
);

export const DAC7_VALIDATION_RULES_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/tax/dac7/validation-rules/'
);

export const DAC7_TAX_INFORMATION_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/tax/dac7/'
);

export const UPDATE_PRODUCT_LIKE_ENDPOINT = buildEndpoint(
  `/presentation/api/v1/users/me/liked-products/:productId/`
);

export const BOOSTED_CLICK_ATTR_ENDPOINT = buildEndpoint(
  `/presentation/api/v1/products/:productId/boost-click/`
);

export const BOOSTED_SHOP_STATUS_ENDPOINT = buildEndpoint(
  '/presentation/api/v2/boost/shop/'
);

export const BOOSTED_LISTINGS_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/shops/me/boosted-products/status/'
);

export const UPDATE_BOOSTED_LISTINGS_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/products/batch/:productIds/boost/'
);

export const BOOSTING_STATS_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/shops/me/boosted-products/stats/'
);

export const USER_ADS_STATS_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/users/:userId/ads/stats/'
);

export const USER_BALANCE = buildEndpoint('/api/v1/stripe/balance/');

export const SAVED_PRODUCTS_ENDPOINT = buildEndpoint(
  '/presentation/api/v3/users/me/saved-products/'
);

export const LIKED_PRODUCTS_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/shops/:userId/likes/'
);

export const BRANDS_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/attributes/brands/'
);

export const BRANDS_BY_LETTER_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/attributes/brands/by-letter/:letter/'
);

export const BRANDS_BATCH_BY_ID_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/attributes/brands/batch/by-id/:ids/'
);

export const BRANDS_BATCH_BY_SLUG_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/attributes/brands/batch/by-slug/:slugs/'
);

export const USER_FEEDBACK_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/users/:userId/feedback/'
);

export const CREATE_FEEDBACK_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/users/me/feedback/'
);

export const CREATE_PRODUCT_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/listing/products/'
);

export const BULK_POST_DRAFTS_ASYNC_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/drafts/products/'
);

export const BULK_POST_DRAFTS_ASYNC_STATUS_ENDPOINT = buildEndpoint(
  `/api/v3/drafts/products/:id/status/`
);
export const UPDATE_PRODUCT_BY_SLUG = buildEndpoint(
  '/presentation/api/v1/products/by-slug/:slug/'
);

export const REPOP_PRODUCT_ENDPOINT = buildEndpoint(
  `/presentation/api/v1/products/repop/by-purchase/:purchaseId/by-item/:purchaseItemId/`
);

export const REPOP_PICTURE_ENDPOINT = buildEndpoint(
  `/presentation/api/v1/listing/:pictureId/repop/`
);

export const REPOP_PRODUCTS = buildEndpoint(
  '/presentation/api/v1/products/repop/'
);

export const DRAFTS_BATCH_POST_ENDPOINT = buildEndpoint(
  `/presentation/api/v1/drafts/batch/`
);

export const DELETE_DRAFTS_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/drafts/batch/:draftIds/'
);

export const DRAFTS_ENDPOINT = buildEndpoint(`/presentation/api/v1/drafts/`);

export const DRAFTS_BATCH_UPDATE_ENDPOINT = buildEndpoint(
  `/presentation/api/v1/drafts/batch/`
);

export const CREATE_DRAFT_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/drafts/'
);

export const DRAFTS_BY_ID_PRESENTATION = buildEndpoint(
  '/presentation/api/v1/drafts/:draftId/'
);

export const DRAFT_BY_SKU = buildEndpoint(
  `/presentation/api/v1/drafts/by-sku/:sku/`
);

export const ADDRESS_SEARCH_TEXT = buildEndpoint(
  `/presentation/api/v1/addresses/search/text/`
);

export const ADDRESS_SEARCH_REFINED_RESULTS = buildEndpoint(
  `/presentation/api/v1/addresses/search/:id/results/`
);

export const ADDRESS_SEARCH_DETAIL = buildEndpoint(
  `/presentation/api/v1/addresses/search/:id/detail/`
);

export const GUEST_ADDRESS_SEARCH_TEXT = buildEndpoint(
  `/presentation/api/v1/addresses/search/guest/text/`
);

export const GUEST_ADDRESS_SEARCH_REFINED_RESULTS = buildEndpoint(
  `/presentation/api/v1/addresses/search/guest/:id/results/`
);

export const GUEST_ADDRESS_SEARCH_DETAIL = buildEndpoint(
  `/presentation/api/v1/addresses/search/guest/:id/detail/`
);

export const CATEGORY_BREADCRUMBS = buildEndpoint(
  `/presentation/api/v1/attributes/breadcrumbs/`
);

export const CATEGORY_SEARCH_SUGGESTIONS = buildEndpoint(
  `/presentation/api/v1/search/category/suggestions/`
);

export const USER_ADDRESSES = buildEndpoint(`/presentation/api/v1/addresses/`);

export const USER_ADDRESSES_V2 = buildEndpoint(
  `/presentation/api/v2/addresses/`
);

export const USER_ADDRESSES_LIST_V2 = buildEndpoint(
  `/presentation/api/v2/addresses/user/:userId/`
);

export const USER_ADDRESS_BY_ID = buildEndpoint(
  `/presentation/api/v1/addresses/:addressId/`
);

export const USER_UPDATE_ADDRESS_V2 = buildEndpoint(
  `/presentation/api/v2/addresses/:addressId/`
);

export const USER_DELETE_ADDRESS = buildEndpoint(
  `/presentation/api/v1/addresses/:addressId/`
);

export const SHOP_PRODUCTS_BY_STATUS_ENDPOINT = buildEndpoint(
  `/presentation/api/v1/shops/:sellerId/products/by-status/:status/`
);

export const LEGACY_PRODUCT_BY_ID = buildEndpoint(
  `/api/v2/product/by-id/:productId/`
);

export const PRODUCT_BY_ID_ENDPOINT = buildEndpoint(
  `/presentation/api/v1/products/:productId/`
);

export const PRODUCT_BY_SLUG_EXTENDED = buildEndpoint(
  `/presentation/api/v1/products/by-slug/:slug/extended/`
);

export const PRODUCT_BY_SLUG = buildEndpoint(
  `/presentation/api/v1/products/by-slug/:slug/`
);

export const USER_PRODUCT_BY_SLUG = buildEndpoint(
  `/api/v1/product/by-slug/:slug/user/`
);

export const PRODUCT_BY_SLUG_LISTING = buildEndpoint(
  `/presentation/api/v1/products/by-slug/:slug/edit-listing/`
);

export const PRODUCT_BY_SLUG_LISTING_SIMILAR = buildEndpoint(
  `/presentation/api/v1/products/by-slug/:slug/listing/similar/`
);

export const PRODUCT_MORE_ITEMS = buildEndpoint(
  '/presentation/api/v1/products/:id/more-items/'
);

export const PERFORMANCE_METRICS = buildEndpoint(
  `/presentation/api/v1/performance/collect/`
);

export const SINGLE_OUTFIT_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/outfits/:outfitId/'
);

export const UPDATE_OUTFIT_ENDPOINT = buildEndpoint(
  '/presentation/api/v2/outfits/:outfitId/'
);

export const PAGINATED_OUTFITS_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/outfits/'
);

export const CREATE_PICTURE = buildEndpoint('/presentation/api/v1/pictures/');

export const FETCH_BATCH_PICTURES_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/pictures/batch/:pictureIds/'
);

export const VALIDATE_PICTURES_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/pictures/validate/'
);

export const COLOUR_PREDICTION = buildEndpoint(
  '/presentation/api/v1/attributes/colours/prediction/'
);

export const AWAITING_SHIPPING_COUNT = buildEndpoint(
  '/presentation/api/v1/shipping/me/sales/counters/awaiting-shipping/'
);

export const BRAND_PREDICTION = buildEndpoint(
  '/presentation/api/v1/attributes/brands/prediction/'
);

export const CATEGORY_PREDICTION = buildEndpoint(
  '/presentation/api/v1/attributes/categories/prediction/'
);

export const DESCRIPTION_PREDICTION = buildEndpoint(
  '/presentation/api/v1/attributes/descriptions/prediction/'
);

export const CREATE_OUTFIT_ENDPOINT = buildEndpoint(
  '/presentation/api/v2/outfits/'
);

export const OUTFITS_CONFIG_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/outfits/config/'
);
export const OUTFITS_USER_CONFIG_ENDPOINT = buildEndpoint(
  '/presentation/api/v2/users/:userId/outfits/config/'
);

export const DELETE_BATCH_OUTFITS_ENDPOINT = buildEndpoint(
  '/api/v1/outfits/delete/'
);

export const LIKE_OUTFIT_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/outfits/:outfitId/like/'
);

export const COLLAGE_ITEMS = buildEndpoint(
  '/presentation/api/v1/outfits/collage-items/:category/'
);

export const COLLAGE_PRODUCTS_CATEGORIES = buildEndpoint(
  '/presentation/api/v1/outfits/collage-items/meta/tabs/'
);

export const COLLAGE_CUTOUTS_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/outfits/collage-items/cutouts/'
);

export const OUTFIT_TEMPLATES_PRESENTATION_LIST_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/outfits/template/'
);

export const OUTFIT_TEMPLATES_PRESENTATION_BY_ID_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/outfits/template/:templateId/'
);

export const OUTFIT_TEMPLATES_PRESENTATION_REORDER_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/outfits/template/reorder/'
);

export const STORIES_CONFIG_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/stories/config/'
);

export const STORIES_BY_USERNAME_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/stories/feed/by-username/:username/'
);

export const DELETE_STORY_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/stories/:story_id/'
);

export const SELLER_PRODUCTS_LISTED = buildEndpoint(
  '/presentation/api/v1/users/me/product-counts/'
);

export const SELLER_PRODUCTS = buildEndpoint('/api/v1/shop/products/');

export const RELIST_PRODUCTS = buildEndpoint(
  '/presentation/api/v1/products/reprice/eligible-products/'
);

export const REPRICE_PRODUCTS_SUMMARY = buildEndpoint(
  '/presentation/api/v1/products/reprice/batch/:productIds/summary/'
);

export const REPRICE_PRODUCTS_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/products/reprice/'
);

export const MAGIC_LINK_REQUEST_ENDPOINT = buildTurnstileEnabledEndpoint(
  '/presentation/api/v1/login/magic-link/'
);

export const MAGIC_LINK_REQUEST_SYNC_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/login/magic-link/sync/'
);

export const MAGIC_LINK_REGISTER_ENDPOINT = buildTurnstileEnabledEndpoint(
  '/presentation/api/v1/signup/magic-link/'
);

export const LOGIN = buildEndpoint('/presentation/api/v1/login/');

export const BRAND_PAGE_BY_SLUG_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/attributes/brands/by-slug/:slug/'
);

export const CATEGORIES_ENDPOINT = buildEndpoint('/api/v1/categories/');

export const CATEGORY_SIZE_MAP_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/attributes/categories/size-mapping/'
);

export const CHECKOUT_SUMMARY_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/purchases/summary/'
);

export const START_PURCHASE_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/purchases/start/'
);
export const CANCEL_PURCHASE_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/purchases/:purchaseId/cancel/'
);
export const COMPLETE_PURCHASE_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/purchases/:purchaseId/complete/'
);
export const PURCHASE_COUNTS_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/purchases/with-user/:userId/'
);

export const SIMILAR_ITEMS_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/products/:productId/similar/'
);

export const UPDATE_SAVED_PRODUCT_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/users/me/saved-products/:productId/'
);

export const DELETE_PRODUCTS_ENDPOINT = buildEndpoint('/api/v1/products/');

export const GET_PRODUCT_BY_SKU_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/products/by-sku/:sku/'
);

export const DELETE_PRODUCT_BY_ID_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/products/:productId/'
);

export const RECENTLY_VIEWED_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/users/me/recently-viewed/'
);

export const BASED_ON_RECENT_VIEWS_AUTH_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/users/me/recently-viewed/recommendations/products/'
);

export const BASED_ON_RECENT_VIEWS_UNAUTH_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/products/recently-viewed/recommendations/products/'
);

export const USERNAME_VALIDITY_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/signup/username-validity/:username/'
);

export const OFFERS_ENDPOINT = buildEndpoint('/presentation/api/v1/offers/');

export const COUNTER_OFFER_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/offers/:offer_id/'
);

export const GUEST_OFFERS_PRICE_QUOTE_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/offers/guest/price-quote/'
);

export const OFFERS_PRICE_QUOTE_V2_ENDPOINT = buildEndpoint(
  '/presentation/api/v2/offers/price-quote/'
);

export const COUNTER_OFFERS_PRICE_QUOTE_V2_ENDPOINT = buildEndpoint(
  '/presentation/api/v2/offers/:offerId/price-quote/'
);

export const BUYER_ACTIVE_OFFERS_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/offers/me/active/'
);

export const BUYER_INACTIVE_OFFERS_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/offers/me/inactive/'
);

export const DISMISS_OFFER_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/offers/:offerId/dismiss/'
);

export const SIGNUP_ENDPOINT = buildEndpoint('/presentation/api/v1/signup/');

export const MARKETING_CONSENT_PREFERENCES_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/consent/preferences/'
);

export const SIGNUP_USERNAME_SUGGESTIONS_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/signup/usernames/suggestions/'
);

export const AGE_VERIFICATION_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/signup/age-verification/'
);

export const AGE_VERIFICATION_STATUS_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/signup/age-verification/:ageCheckId/'
);

export const MARKETING_CONSENT_ENDPOINT = buildEndpoint('/api/v1/consent/');

export const MARKETING_UNSUBSCRIBE_ENDPOINT = buildEndpoint(
  '/api/v2/consent/unsubscribe/email'
);

export const FETCH_PAYMENT_PROVIDERS_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/purchases/providers/by-seller-id/:sellerId/'
);

export const CONVERSATIONS_LIST = buildEndpoint(
  '/presentation/api/v1/conversations/'
);

export const FETCH_CONVERSATION_MESSAGES = buildEndpoint(
  '/presentation/api/v1/conversations/:conversationId/messages/'
);

export const FETCH_CONVERSATION_BY_ID = buildEndpoint(
  '/presentation/api/v1/conversations/:conversationId/'
);

export const FETCH_CONVERSATION_BY_USER_ID = buildEndpoint(
  '/presentation/api/v1/conversations/me/with-user/:userId/'
);

export const HIDE_CONVERSATION_BY_ID = buildEndpoint(
  '/presentation/api/v1/conversations/hide/'
);

export const MARK_CONVERSATION_AS_READ = buildEndpoint(
  '/presentation/api/v1/conversations/mark-read/'
);

export const MARK_CONVERSATION_AS_UNREAD = buildEndpoint(
  '/presentation/api/v1/conversations/mark-unread/'
);

export const CHAT_VERIFICATION = buildEndpoint(
  '/presentation/api/v1/conversations/verification/'
);

export const SEND_MESSAGE = buildTurnstileEnabledEndpoint(
  '/presentation/api/v1/conversations/messages/'
);

export const LISTING_COUNTRIES = buildStaticUrl(
  '/web/assets/listing/location/countries.json'
);

export const LISTING_CITIES = buildStaticUrl(
  '/web/assets/listing/location/:countryCode'
);

export const FETCH_TEMPLATES = buildEndpoint(
  '/presentation/api/v1/drafts/templates/'
);

export const ZENDESK_JWT_ENDPOINT = buildEndpoint('/api/v1/zendesk/auth/');

export const USERS_SEARCH_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/search/users/'
);

export const SUGGESTED_OFFERS = buildEndpoint(
  '/presentation/api/v1/products/:productId/suggested/'
);

export const SELLER_PRODUCTS_WITH_OFFERS = buildEndpoint(
  '/presentation/api/v1/offers/me/products/'
);

export const SELLER_OFFERS_FOR_PRODUCT = buildEndpoint(
  '/presentation/api/v1/products/:productId/offers/'
);

export const SELLER_LIKES_NOTIFICATIONS = buildEndpoint(
  '/api/v1/user/likes/notifications/'
);

export const SELLER_OFFER_RESPONSE = buildEndpoint(
  '/presentation/api/v1/products/:productId/offers/:offerId/'
);

export const MFA_STATUS_ENDPOINT = buildEndpoint(
  '/presentation/api/v2/users/me/mfa-status/'
);

export const SIMILAR_SOLD_ENDPOINT = buildEndpoint(
  '/api/v2/analytics/products/pricing-inspiration/'
);

export const MFA_CHALLENGE_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/login/mfa/challenges/:challengeId/attempt/'
);

export const SELLER_START_SEND_OFFER_ALL = buildEndpoint(
  '/presentation/api/v1/offers/products/:productId/offers/send-all/'
);

export const SELLER_SEND_OFFER_ALL = buildEndpoint(
  '/presentation/api/v1/offers/products/:productId/offers/send-all/jobs/'
);

export const SELLER_SEND_OFFER_ALL_STATUS = buildEndpoint(
  '/presentation/api/v1/offers/products/:productId/offers/send-all/jobs/:jobId/'
);

export const SELLER_CAN_SET_AUTO_SEND_OFFER_SETTINGS = buildEndpoint(
  '/presentation/api/v1/offers/seller/can-set-auto-send-offers-settings/:productId/'
);

export const SELLER_AUTO_SEND_OFFER_SETTINGS = buildEndpoint(
  '/presentation/api/v1/offers/seller/auto-send-offers-settings/:productId/'
);

export const DISPUTES_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/disputes/'
);

export const DISPUTE_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/disputes/:disputeId/'
);

export const DISPUTE_ACTION_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/disputes/:disputeId/action/'
);

export const DISPUTE_TASK_ATTACHMENTS_ENDPOINT = buildEndpoint(
  '/internal/v1/disputes/:disputeId/task/:taskId/attachments'
);

export const CONSENT_RECONCILIATION_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/consent/reconciliation/'
);

export const PRODUCT_ACTIONS_ENDPOINT = buildEndpoint(
  '/api/v1/shop/products/actions/'
);

export const CREATE_SHOP_IMPORT_ENDPOINT = buildEndpoint(
  '/presentation/api/v2/crosslist/import/'
);

export const IMPORT_STATUS_V2_ENDPOINT = buildEndpoint(
  '/presentation/api/v2/crosslist/import/:jobId/'
);

export const OAUTH_CLIENT_VALIDATE_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/oauth/authorize/validate/'
);

export const OAUTH_CLIENT_AUTHORIZE_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/oauth/authorize/'
);

export const CONNECTED_APPS_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/oauth/connected-partner-apps/'
);

export const REVOKE_CONNECTED_APP_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/oauth/connected-partner-apps/:grant_id/revoke/'
);

export const PAYMENT_SETTINGS_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/users/me/payment-settings/'
);

export const STRIPE_TAX_VERIFICATION_URL_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/users/me/tax/stripe-verification-url/'
);

export const PAYOUT_SETTINGS_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/users/me/payout-settings/'
);

export const SCHEDULED_DROPS_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/scheduled-drops/'
);

export const SCHEDULED_DROPS_DROP_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/scheduled-drops/:drop_id/'
);

export const SCHEDULED_DROPS_DRAFTS_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/scheduled-drops/:drop_id/drafts/'
);

export const BANNED_HASHTAGS_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/listing/banned-hashtags/'
);

export const SCREEN_BANNERS_ENDPOINT = buildEndpoint(
  '/presentation/api/v1/bedui/screens/:screen/banner/'
);
